import {
  OrderCompletionBlockedReason,
  OrderProgressStage,
  OrderProgressStageState,
  OrderProgressStageStatus,
  OrderProgressWaitingOn,
  OrderWorkProofCompletedStatus,
  OrderWorkProofOpenStatus,
} from 'bff';
import {
  IconBan,
  IconCalendarEvent,
  IconCamera,
  IconCheck,
  IconFiles,
  IconFlagCheck,
  IconHourglass,
  IconLoader2,
  IconLock,
  IconMinus,
  IconSend,
  IconUserCheck,
  IconUserExclamation,
  IconX,
} from '@tabler/icons-react';

/**
 * order_progress presentation tables. Every one is a COMPLETE `Record` over the
 * contract unions: a new stage or status breaks the build here until mapped.
 *
 * `work_proof` solo llega en las órdenes que se prueban con inspección: lo que
 * no llega, no se dibuja.
 */

export type OrderProgressAudience = 'insurer' | 'provider' | 'customer';

// Audiences that can OWE the next action. The customer only watches, so the
// imperative "what is missing now" copy is never written for them.
export type OrderProgressActionAudience = Exclude<
  OrderProgressAudience,
  'customer'
>;

// Node icon while the stage has no outcome yet: it identifies the stage. Stages
// with an outcome use the STATUS_MARK glyph.
export const ORDER_PROGRESS_STAGE_ICON: Record<
  OrderProgressStage,
  React.ElementType
> = {
  created: IconSend,
  accepted: IconUserCheck,
  scheduling: IconCalendarEvent,
  work_proof: IconCamera,
  documentation: IconFiles,
  completed: IconFlagCheck,
};

// Status glyph. One of the node's three redundant channels (glyph + visible
// label + sr-only status word): color never carries the meaning alone.
export const ORDER_PROGRESS_STATUS_MARK: Record<
  OrderProgressStageStatus,
  React.ElementType
> = {
  done: IconCheck,
  awaiting_provider: IconUserExclamation,
  awaiting_insurer: IconHourglass,
  in_progress: IconLoader2,
  rejected: IconX,
  blocked: IconLock,
  unreachable: IconMinus,
  canceled: IconBan,
};

// Statuses that identify the stage instead of its outcome use the stage icon:
// the node says "the document goes here", not "nothing happened here".
export const ORDER_PROGRESS_USES_STAGE_ICON: Record<
  OrderProgressStageStatus,
  boolean
> = {
  done: false,
  awaiting_provider: true,
  awaiting_insurer: true,
  in_progress: true,
  rejected: false,
  blocked: true,
  unreachable: true,
  canceled: false,
};

interface StatusTone {
  // The node circle.
  node: string;
  // Color of the connector leaving the node; the stepper decides solid/dashed
  // from whether the stage was already gone through.
  connector: string;
  // Node label and sub-label.
  label: string;
}

// Palette aligned with AlertComponent's `alertVariants`, so the app does not
// invent new colors.
export const ORDER_PROGRESS_STATUS_TONE: Record<
  OrderProgressStageStatus,
  StatusTone
> = {
  done: {
    node: 'border-green-800/60 bg-green-100 text-green-800',
    connector: 'border-green-800/50',
    label: 'text-green-800',
  },
  awaiting_provider: {
    node: 'border-yellow-800/60 bg-yellow-100 text-yellow-800',
    connector: 'border-yellow-800/40',
    label: 'text-yellow-800',
  },
  awaiting_insurer: {
    node: 'border-sky-800/60 bg-sky-100 text-sky-800',
    connector: 'border-sky-800/40',
    label: 'text-sky-800',
  },
  in_progress: {
    node: 'border-sky-800/60 bg-sky-100 text-sky-800',
    connector: 'border-sky-800/40',
    label: 'text-sky-800',
  },
  rejected: {
    node: 'border-red-800/60 bg-red-100 text-red-800',
    connector: 'border-red-800/40',
    label: 'text-red-800',
  },
  blocked: {
    node: 'border-muted-foreground/40 bg-muted text-muted-foreground',
    connector: 'border-muted-foreground/30',
    label: 'text-muted-foreground',
  },
  unreachable: {
    node: 'border-dashed border-muted-foreground/30 bg-transparent text-muted-foreground/60',
    connector: 'border-muted-foreground/20',
    label: 'text-muted-foreground/60',
  },
  canceled: {
    node: 'border-muted-foreground/50 bg-muted text-muted-foreground',
    connector: 'border-muted-foreground/30',
    label: 'text-muted-foreground',
  },
};

// A completed work proof is `done` whatever its verdict, so the verdict picks
// the tone: green only when Autoinspector approved it.
const ORDER_PROGRESS_VERDICT_TONE: Record<
  OrderWorkProofCompletedStatus,
  StatusTone
> = {
  approved: ORDER_PROGRESS_STATUS_TONE.done,
  disapproved: ORDER_PROGRESS_STATUS_TONE.rejected,
  error: ORDER_PROGRESS_STATUS_TONE.rejected,
};

export const orderProgressStageTone = (
  state: OrderProgressStageState,
): StatusTone =>
  state.verdict
    ? ORDER_PROGRESS_VERDICT_TONE[state.verdict]
    : ORDER_PROGRESS_STATUS_TONE[state.status];

// i11n key suffixes under `orders.progress.`.
export const ORDER_PROGRESS_STAGE_LABEL_KEY: Record<
  OrderProgressStage,
  string
> = {
  created: 'stage.created',
  accepted: 'stage.accepted',
  scheduling: 'stage.scheduling',
  work_proof: 'stage.work_proof',
  documentation: 'stage.documentation',
  completed: 'stage.completed',
};

// Tab where each stage is resolved, relative to the order detail. The stages
// without a tab of their own land on the detail itself.
export const ORDER_PROGRESS_STAGE_TAB: Record<OrderProgressStage, string> = {
  created: '',
  accepted: '',
  scheduling: '/scheduling',
  work_proof: '/inspections',
  documentation: '/documentation',
  completed: '',
};

// The tab slugs are shared, the prefix is not: each app injects `basePath`
// because the insurer nests the order under the claim and the provider does not.
export const orderProgressStageHref = (
  basePath: string,
  stage: OrderProgressStage,
): string => `${basePath}${ORDER_PROGRESS_STAGE_TAB[stage]}`;

// The status word changes with the side it is read from: "your turn" for whoever
// has to act, "waiting on the provider" for whoever watches.
const ORDER_PROGRESS_STATUS_LABEL_KEY: Record<
  OrderProgressStageStatus,
  Record<OrderProgressAudience, string>
> = {
  done: {
    insurer: 'status.done',
    provider: 'status.done',
    customer: 'status.done',
  },
  awaiting_provider: {
    insurer: 'status.awaiting_provider.insurer',
    provider: 'status.awaiting_provider.provider',
    customer: 'status.awaiting_provider.customer',
  },
  awaiting_insurer: {
    insurer: 'status.awaiting_insurer.insurer',
    provider: 'status.awaiting_insurer.provider',
    customer: 'status.awaiting_insurer.customer',
  },
  in_progress: {
    insurer: 'status.in_progress',
    provider: 'status.in_progress',
    customer: 'status.in_progress',
  },
  rejected: {
    insurer: 'status.rejected',
    provider: 'status.rejected',
    customer: 'status.rejected',
  },
  blocked: {
    insurer: 'status.blocked',
    provider: 'status.blocked',
    customer: 'status.blocked',
  },
  unreachable: {
    insurer: 'status.unreachable',
    provider: 'status.unreachable',
    customer: 'status.unreachable',
  },
  canceled: {
    insurer: 'status.canceled',
    provider: 'status.canceled',
    customer: 'status.canceled',
  },
};

// Reviewed stages are not "completed": they are approved or disapproved, and the
// copy agrees in gender with its noun. Anything absent falls back to the generic
// ORDER_PROGRESS_STATUS_LABEL_KEY.
const ORDER_PROGRESS_STAGE_STATUS_LABEL_KEY: Partial<
  Record<OrderProgressStage, Partial<Record<OrderProgressStageStatus, string>>>
> = {
  // Scheduling is not "completed" nor "queued": it is recorded or missing.
  scheduling: {
    done: 'statusByStage.scheduling.done',
    awaiting_provider: 'statusByStage.scheduling.awaiting_provider',
  },
  // A completed work proof is labelled by its verdict (see below).
  work_proof: {
    rejected: 'statusByStage.work_proof.rejected',
    // The insurer does not "review" the inspection: it creates it.
    awaiting_insurer: 'statusByStage.work_proof.awaiting_insurer',
  },
  // order_required_docs — the documentation is loaded/complete, not "finished";
  // rejected means a document must be re-uploaded.
  documentation: {
    done: 'statusByStage.documentation.done',
    rejected: 'statusByStage.documentation.rejected',
  },
};

export const orderProgressStatusLabelKey = (
  stage: OrderProgressStage,
  status: OrderProgressStageStatus,
  audience: OrderProgressAudience,
): string =>
  ORDER_PROGRESS_STAGE_STATUS_LABEL_KEY[stage]?.[status] ??
  ORDER_PROGRESS_STATUS_LABEL_KEY[status][audience];

// "Done" alone would read as approved: a completed work proof names its verdict.
const ORDER_PROGRESS_VERDICT_LABEL_KEY: Record<
  OrderWorkProofCompletedStatus,
  string
> = {
  approved: 'verdict.approved',
  disapproved: 'verdict.disapproved',
  error: 'verdict.error',
};

export const orderProgressStageStatusLabelKey = (
  state: OrderProgressStageState,
  audience: OrderProgressAudience,
): string =>
  state.verdict
    ? ORDER_PROGRESS_VERDICT_LABEL_KEY[state.verdict]
    : orderProgressStatusLabelKey(state.stage, state.status, audience);

// A newer inspection still open after the one that completed the stage (the
// automatic retry). Informative: it never blocks the order.
export const ORDER_PROGRESS_OPEN_ATTEMPT_LABEL_KEY: Record<
  OrderWorkProofOpenStatus,
  Record<OrderProgressAudience, string>
> = {
  pending_capture: {
    insurer: 'openAttempt.pending_capture.insurer',
    provider: 'openAttempt.pending_capture.provider',
    customer: 'openAttempt.pending_capture.customer',
  },
  capturing: {
    insurer: 'openAttempt.capturing.insurer',
    provider: 'openAttempt.capturing.provider',
    customer: 'openAttempt.capturing.customer',
  },
  processing: {
    insurer: 'openAttempt.processing.insurer',
    provider: 'openAttempt.processing.provider',
    customer: 'openAttempt.processing.customer',
  },
};

// Statuses that can be a `next_action` (the same ones as the backend WAITING_ON).
export type OrderProgressActionableStatus = Extract<
  OrderProgressStageStatus,
  'awaiting_provider' | 'awaiting_insurer' | 'rejected' | 'in_progress'
>;

// `created` is never actionable (always `done`), so it stays out.
export type OrderProgressActionableStage = Exclude<
  OrderProgressStage,
  'created'
>;

// The "what is missing now" phrase. `null` marks a combination the backend does
// not produce: declared explicitly instead of inventing copy for it.
export const ORDER_PROGRESS_NEXT_ACTION_KEY: Record<
  OrderProgressActionableStage,
  Record<
    OrderProgressActionableStatus,
    Record<OrderProgressActionAudience, string> | null
  >
> = {
  accepted: {
    awaiting_provider: {
      insurer: 'next.accepted.awaiting_provider.insurer',
      provider: 'next.accepted.awaiting_provider.provider',
    },
    // The order only waits on the provider to be accepted, and a rejection is
    // terminal (next_action stays null).
    awaiting_insurer: null,
    rejected: null,
    in_progress: null,
  },
  // order_scheduling — claimed right after the acceptance: the work is
  // coordinated before it is performed.
  scheduling: {
    awaiting_provider: {
      insurer: 'next.scheduling.awaiting_provider.insurer',
      provider: 'next.scheduling.awaiting_provider.provider',
    },
    // Scheduling is never reviewed, rejected, nor advanced off-platform.
    awaiting_insurer: null,
    rejected: null,
    in_progress: null,
  },
  work_proof: {
    awaiting_provider: {
      insurer: 'next.work_proof.awaiting_provider.insurer',
      provider: 'next.work_proof.awaiting_provider.provider',
    },
    // The order requires the proof and the inspection does not exist yet: the
    // automatic creation on accept failed and the insurer has to create it.
    awaiting_insurer: {
      insurer: 'next.work_proof.awaiting_insurer.insurer',
      provider: 'next.work_proof.awaiting_insurer.provider',
    },
    rejected: {
      insurer: 'next.work_proof.rejected.insurer',
      provider: 'next.work_proof.rejected.provider',
    },
    in_progress: {
      insurer: 'next.work_proof.in_progress',
      provider: 'next.work_proof.in_progress',
    },
  },
  // order_required_docs — pending required documents; the side owing the next
  // upload (or the review) is the one that acts.
  documentation: {
    awaiting_provider: {
      insurer: 'next.documentation.awaiting_provider.insurer',
      provider: 'next.documentation.awaiting_provider.provider',
    },
    awaiting_insurer: {
      insurer: 'next.documentation.awaiting_insurer.insurer',
      provider: 'next.documentation.awaiting_insurer.provider',
    },
    rejected: {
      insurer: 'next.documentation.rejected.insurer',
      provider: 'next.documentation.rejected.provider',
    },
    in_progress: null,
  },
  completed: {
    // Finalizing is always an explicit insurer action.
    awaiting_provider: null,
    awaiting_insurer: {
      insurer: 'next.completed.awaiting_insurer.insurer',
      provider: 'next.completed.awaiting_insurer.provider',
    },
    rejected: null,
    in_progress: null,
  },
};

// Why the "Finalizar orden" button is disabled. Absolute keys: consumed outside
// the `orders.progress` scope.
export const COMPLETION_BLOCKED_REASON_KEY: Record<
  OrderCompletionBlockedReason,
  string
> = {
  work_proof_not_completed: 'orders.review.gate.workProofNotCompleted',
  not_scheduled: 'orders.review.gate.notScheduled',
  documentation_incomplete: 'orders.review.gate.documentationIncomplete',
};

export const ORDER_PROGRESS_WAITING_ON_LABEL_KEY: Record<
  OrderProgressWaitingOn,
  string
> = {
  provider: 'waitingOn.provider',
  insurer: 'waitingOn.insurer',
  external: 'waitingOn.external',
};

// La etapa que demuestra el trabajo (`work_proof`) solo la emiten las órdenes
// con mecanismo de inspección. Quien necesite razonar sobre "¿ya está demostrado
// el trabajo?" la busca con esto en vez de preguntar por el mecanismo.
export const findProofStage = (
  stages: OrderProgressStageState[],
): OrderProgressStageState | null =>
  stages.find((state) => state.stage === 'work_proof') ?? null;

export const isActionableProgressStatus = (
  status: OrderProgressStageStatus,
): status is OrderProgressActionableStatus =>
  status === 'awaiting_provider' ||
  status === 'awaiting_insurer' ||
  status === 'rejected' ||
  status === 'in_progress';
